import { useEffect, useState, useRef, createContext, useContext } from 'react';
import API from '../api';
import { T } from '../i18n';
import LogoMark from '../components/LogoMark';

/* ── Social icons ───────────────────────────────────────────────────────── */
const IconInstagram = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37Z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);
const IconTelegram = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.24 3.64 11.95c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42Z"/>
  </svg>
);
const IconPhone = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z"/>
  </svg>
);

/* ── Lang context ───────────────────────────────────────────────────────── */
const LangCtx = createContext({ lang:'ru', setLang:()=>{} });
const useLang = () => useContext(LangCtx);
const useT = () => { const { lang } = useLang(); return T[lang]; };

/* ── hooks ─────────────────────────────────────────────────────────────── */
function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const o = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setV(true); o.disconnect(); } }, { threshold });
    if (ref.current) o.observe(ref.current);
    return () => o.disconnect();
  }, [threshold]);
  return [ref, v];
}

function Toast({ msg, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3200); return () => clearTimeout(t); }, [onDone]);
  return <div className={`toast toast--${type}`}>{msg}</div>;
}

/* ── Lang switcher ──────────────────────────────────────────────────────── */
function LangSwitch() {
  const { lang, setLang } = useLang();
  return (
    <div className="lang-switch">
      <button className={`lang-btn${lang==='ru'?' lang-btn--active':''}`} onClick={()=>setLang('ru')}>RU</button>
      <span className="lang-div">|</span>
      <button className={`lang-btn${lang==='uz'?' lang-btn--active':''}`} onClick={()=>setLang('uz')}>UZ</button>
    </div>
  );
}

/* ── NAVBAR ─────────────────────────────────────────────────────────────── */
function Navbar({ contacts }) {
  const t = useT();
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const h = () => setSolid(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);
  useEffect(() => { document.body.style.overflow = open ? 'hidden' : ''; return () => { document.body.style.overflow = ''; }; }, [open]);
  const phone = contacts?.phone || '+998 77 252-03-30';
  const links = [
    ['#services-grid', t.nav.services],
    ['#how',           t.nav.how],
    ['#team',          t.nav.team],
    ['#pricing',       t.nav.pricing],
    ['#faq',           t.nav.faq],
    ['#contact',       t.nav.contacts],
  ];
  return (
    <>
      <header>
      <nav className={`nav${solid?' nav--solid':''}`} aria-label="Главная навигация">
        <a href="/" className="nav__logo" aria-label="MasterClean Pro — главная">
          <LogoMark size={36} />
          <span>Master<em>Clean</em> Pro</span>
        </a>
        <ul className="nav__links">
          {links.map(([h,l]) => <li key={h}><a href={h}>{l}</a></li>)}
        </ul>
        <LangSwitch/>
        <a href={`tel:${phone}`} className="nav__cta nav__phone">{t.nav.call}</a>
        <button className="nav__burger" onClick={()=>setOpen(true)} aria-label="Menu">
          <span/><span/><span/>
        </button>
      </nav>
      </header>
      <div className={`drawer${open?' drawer--open':''}`}>
        <button className="drawer__close" onClick={()=>setOpen(false)}>✕</button>
        <div className="drawer__logo"><LogoMark size={36} /><span>Master<em>Clean</em> Pro</span></div>
        {links.map(([h,l]) => <a key={h} href={h} className="drawer__link" onClick={()=>setOpen(false)}>{l}</a>)}
        <LangSwitch/>
        <a href={`tel:${phone}`} className="drawer__call" onClick={()=>setOpen(false)}>{phone}</a>
      </div>
    </>
  );
}

/* ── HERO ───────────────────────────────────────────────────────────────── */
const PHOTOS = {
  heroMain:  'https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?auto=compress&cs=tinysrgb&w=1200&h=800&fit=crop',
  heroSmall: 'https://images.pexels.com/photos/1643383/pexels-photo-1643383.jpeg?auto=compress&cs=tinysrgb&w=480&h=320&fit=crop',
  whyus:     'https://images.pexels.com/photos/4107278/pexels-photo-4107278.jpeg?auto=compress&cs=tinysrgb&w=900&h=600&fit=crop',
  faq:       'https://images.pexels.com/photos/1358912/pexels-photo-1358912.jpeg?auto=compress&cs=tinysrgb&w=600&h=400&fit=crop',
  cta:       'https://images.pexels.com/photos/1571460/pexels-photo-1571460.jpeg?auto=compress&cs=tinysrgb&w=1600&h=900&fit=crop',
  contact:   'https://images.pexels.com/photos/4108715/pexels-photo-4108715.jpeg?auto=compress&cs=tinysrgb&w=700&h=500&fit=crop',
};

function Hero({ contacts }) {
  const t = useT();
  const phone = contacts?.phone || '+998 77 252-03-30';
  return (
    <section className="hero">
      <div className="hero__bg" aria-hidden="true">
        <div className="hero__glow hero__glow--a"/>
        <div className="hero__glow hero__glow--b"/>
        <div className="hero__glow hero__glow--c"/>
        <div className="hero__grid"/>
        <div className="hero__noise"/>
      </div>

      <div className="hero__inner">
        <div className="hero__head">
          <div className="hero__pill">
            <span className="hero__pill-dot"/>
            {t.hero.pill}
          </div>

          <h1 className="hero__h1">
            <span className="hero__h1-line">{t.hero.h1a}</span>
            <span className="hero__h1-line">{t.hero.h1b}</span>
            <span className="hero__h1-line hero__h1-accent">{t.hero.h1accent}</span>
          </h1>

          <p className="hero__sub">{t.hero.sub}</p>

          <ul className="hero__chips" aria-label="features">
            <li><span className="hero__chip-ic">✦</span>{t.hero.chipEco}</li>
            <li><span className="hero__chip-ic">✦</span>{t.hero.chipCertified}</li>
            <li><span className="hero__chip-ic">✦</span>{t.hero.chipInsured}</li>
            <li><span className="hero__chip-ic">✦</span>{t.hero.chipGuarantee}</li>
          </ul>

          <div className="hero__btns">
            <a href="#contact" className="hero__cta">
              <span>{t.hero.order}</span>
              <span className="hero__cta-arrow" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </span>
            </a>
            <a href={`tel:${phone}`} className="hero__call">
              <span className="hero__call-ic" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.86 19.86 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.86 19.86 0 0 1 2.12 4.18 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
              </span>
              <span className="hero__call-text">
                <span className="hero__call-label">{t.hero.callLabel}</span>
                <span className="hero__call-num">{phone}</span>
              </span>
            </a>
          </div>
        </div>

        <div className="hero__bento">
          <div className="bento bento--hero">
            <img src={PHOTOS.heroMain} alt={t.hero.altMain} loading="eager"/>
            <div className="bento--hero__shade"/>
            <div className="bento--hero__overlay">
              <div className="bento--hero__chip">
                <span className="bento--hero__chip-dot"/>
                {t.hero.altMain}
              </div>
              <div className="bento--hero__title">{t.hero.badgeTitle}</div>
            </div>
          </div>

          <div className="bento bento--rating">
            <div className="bento--rating__top">
              <div className="bento--rating__num">4.9</div>
              <div className="bento--rating__stars">
                <div className="bento--rating__star-row">★★★★★</div>
                <span>{t.hero.ratingLabel}</span>
              </div>
            </div>
            <div className="bento--rating__bars" aria-hidden="true">
              <div className="bento--rating__bar"><span style={{width:'95%'}}/></div>
              <div className="bento--rating__bar"><span style={{width:'88%'}}/></div>
              <div className="bento--rating__bar"><span style={{width:'76%'}}/></div>
            </div>
          </div>

          <div className="bento bento--badge">
            <div className="bento--badge__icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l3 6 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z"/></svg>
            </div>
            <div className="bento--badge__title">{t.hero.badgeTitle}</div>
            <div className="bento--badge__sub">{t.hero.badgeSub}</div>
          </div>

          <div className="bento bento--review">
            <div className="bento--review__quote" aria-hidden="true">”</div>
            <p className="bento--review__text">{t.hero.reviewText}</p>
            <div className="bento--review__by">
              <div className="bento--review__avatar">N</div>
              <div className="bento--review__meta">
                <strong>{t.hero.reviewAuthor.replace(/^[—\-\s]+/, '')}</strong>
                <span>★★★★★</span>
              </div>
            </div>
          </div>
        </div>

        <div className="hero__stats">
          <div className="hero__stat"><b>500+</b><span>{t.hero.statClients}</span></div>
          <div className="hero__stat"><b>4.9★</b><span>{t.hero.statRating}</span></div>
          <div className="hero__stat"><b>98%</b><span>{t.hero.statRepeat}</span></div>
          <div className="hero__stat"><b>24ч</b><span>{t.hero.statGuarantee}</span></div>
        </div>
      </div>
    </section>
  );
}

/* ── SERVICES ───────────────────────────────────────────────────────────── */
const SVC_ICONS   = ['🏠','🏢','🔨','🛋','🪟','✨'];
const SVC_ACCENTS = [true,false,false,false,false,false];

function Services() {
  const t = useT();
  const [ref, vis] = useInView(0.04);
  return (
    <section id="services-grid" className="section section--light" ref={ref}>
      <div className="container">
        <div className="svc2-header">
          <div>
            <div className="eyebrow">{t.services.eyebrow}</div>
            <h2 className="section-h2">{t.services.title}</h2>
          </div>
          <a href="#contact" className="btn btn--dark">{t.services.order}</a>
        </div>
        <div className="svc2-grid">
          {t.services.cards.map((s,i) => (
            <a href="#contact" key={i}
              className={`svc2-card${SVC_ACCENTS[i]?' svc2-card--accent':''}${vis?' svc2-card--visible':''}`}
              style={{transitionDelay:`${i*0.08}s`}}>
              <div className="svc2-card__top">
                <span className="svc2-card__icon">{SVC_ICONS[i]}</span>
                {s.badge && <span className="svc2-card__badge">{s.badge}</span>}
              </div>
              <div className="svc2-card__title">{s.title}</div>
              <div className="svc2-card__desc">{s.desc}</div>
              <div className="svc2-card__tags">{s.tags.map(tag=><span key={tag} className="svc2-card__tag">{tag}</span>)}</div>
              <div className="svc2-card__arr">{t.services.orderCard}</div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── HOW ────────────────────────────────────────────────────────────────── */
function How() {
  const t = useT();
  const [ref, vis] = useInView(0.05);
  return (
    <section id="how" className="section section--dark" ref={ref}>
      <div className="container">
        <div className="section-head section-head--light">
          <div className="eyebrow eyebrow--teal">{t.how.eyebrow}</div>
          <h2 className="section-h2 section-h2--white">{t.how.title}</h2>
        </div>
        <div className="how-grid">
          {t.how.steps.map(([n,title,desc],i) => (
            <div key={n} className={`how-card${vis?' how-card--visible':''}`} style={{transitionDelay:`${i*0.12}s`}}>
              <div className="how-card__num">{n}</div>
              <h3 className="how-card__title">{title}</h3>
              <p className="how-card__desc">{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── WHY US ─────────────────────────────────────────────────────────────── */
function WhyUs() {
  const t = useT();
  const [ref, vis] = useInView(0.05);
  return (
    <section className="section section--white" ref={ref}>
      <div className="container">
        <div className="whyus">
          <div className="whyus__img-col">
            <div className="whyus__img-frame">
              <img src={PHOTOS.whyus} alt={t.whyus.altTeam} className="whyus__img" loading="lazy"/>
              <div className="whyus__img-chip">
                <div className="whyus__img-chip-num">500+</div>
                <div className="whyus__img-chip-lbl">{t.whyus.chipLbl.split('\n').map((line,i)=><span key={i}>{line}{i===0?<br/>:''}</span>)}</div>
              </div>
            </div>
          </div>
          <div className="whyus__text-col">
            <div className="eyebrow">{t.whyus.eyebrow}</div>
            <h2 className="section-h2">{t.whyus.title.split('\n').map((line,i)=><span key={i}>{line}{i===0?<br/>:''}</span>)}</h2>
            <p className="section-p" style={{marginBottom:36}}>{t.whyus.sub}</p>
            <div className="feats">
              {t.whyus.feats.map(([ic,title,desc],i) => (
                <div key={title} className={`feat${vis?' feat--visible':''}`} style={{transitionDelay:`${0.1+i*0.1}s`}}>
                  <div className="feat__icon">{ic}</div>
                  <div><strong className="feat__title">{title}</strong><p className="feat__desc">{desc}</p></div>
                </div>
              ))}
            </div>
            <a href="#contact" className="btn btn--dark" style={{marginTop:32}}>{t.whyus.order}</a>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── TEAM ───────────────────────────────────────────────────────────────── */
const TEAM = [
  { name:'Гульнора Рашидова',  nameUz:'Gulnora Rashidova',   role:'Старший клинер',          roleUz:'Katta tozalovchi',                    exp:8, rev:112, rating:'5.0', init:'ГР', g:'#00C2B8,#007a75' },
  { name:'Феруза Юсупова',     nameUz:'Feruza Yusupova',     role:'Клинер',                   roleUz:'Tozalovchi',                          exp:5, rev:87,  rating:'4.9', init:'ФЮ', g:'#3b82f6,#1d4ed8' },
  { name:'Дилноза Каримова',   nameUz:'Dilnoza Karimova',    role:'Клинер',                   roleUz:'Tozalovchi',                          exp:6, rev:94,  rating:'5.0', init:'ДК', g:'#8b5cf6,#6d28d9' },
  { name:'Мадина Хасанова',    nameUz:'Madina Xasanova',     role:'Химчистка мебели',         roleUz:'Mebel kimyoviy tozalash',         exp:4, rev:71,  rating:'4.8', init:'МХ', g:'#f59e0b,#d97706' },
  { name:'Нилуфар Собирова',   nameUz:'Nilufar Sobirova',    role:'Клинер',                   roleUz:'Tozalovchi',                          exp:3, rev:58,  rating:'4.9', init:'НС', g:'#10b981,#059669' },
  { name:'Зулайхо Тошматова',  nameUz:'Zulaykho Toshmatova', role:'Старший клинер',           roleUz:'Katta tozalovchi',                    exp:7, rev:103, rating:'5.0', init:'ЗТ', g:'#ef4444,#dc2626' },
];

function Team() {
  const t = useT();
  const { lang } = useLang();
  const [ref, vis] = useInView(0.04);
  return (
    <section id="team" className="section section--dark" ref={ref}>
      <div className="container">
        <div className="section-head section-head--light">
          <div className="eyebrow eyebrow--teal">{t.team.eyebrow}</div>
          <h2 className="section-h2 section-h2--white">{t.team.title}</h2>
          <p className="section-p" style={{color:'rgba(255,255,255,.38)'}}>{t.team.sub}</p>
        </div>
        <div className="team-grid">
          {TEAM.map((m,i) => (
            <div key={m.name} className={`team-card${vis?' team-card--visible':''}`} style={{transitionDelay:`${i*0.09}s`}}>
              <div className="team-card__avatar" style={{background:`linear-gradient(135deg,${m.g})`}}>{m.init}</div>
              <div className="team-card__name">{lang==='uz'?m.nameUz:m.name}</div>
              <div className="team-card__role">{lang==='uz'?m.roleUz:m.role}</div>
              <div className="team-card__stats">
                {[[m.exp,t.team.statExp],[m.rev,t.team.statRev],['★'+m.rating,t.team.statRating]].map(([v,l]) => (
                  <div key={l} className="team-card__stat"><strong>{v}</strong><span>{l}</span></div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── REVIEWS ────────────────────────────────────────────────────────────── */
function Reviews() {
  const t = useT();
  const [ref, vis] = useInView(0.05);
  return (
    <section id="reviews" className="section section--dark" ref={ref}>
      <div className="container">
        <div className="section-head section-head--light">
          <div className="eyebrow eyebrow--teal">{t.reviews.eyebrow}</div>
          <h2 className="section-h2 section-h2--white">{t.reviews.title}</h2>
        </div>
        <div className="reviews-grid">
          {t.reviews.items.map((r,i) => (
            <div key={r.name} className={`review-card${vis?' review-card--visible':''}`} style={{transitionDelay:`${i*0.13}s`}}>
              <div className="review-card__stars">★★★★★</div>
              <p className="review-card__text">«{r.text}»</p>
              <div className="review-card__author">
                <div className="review-card__avatar" style={{background:`linear-gradient(135deg,${r.g})`}}>{r.init}</div>
                <div>
                  <strong className="review-card__name">{r.name}</strong>
                  <span className="review-card__loc">{t.reviews.loc}, {r.loc}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── PRICING ────────────────────────────────────────────────────────────── */
function Pricing({ plans }) {
  const t = useT();
  const { lang } = useLang();
  const [ref, vis] = useInView(0.05);
  return (
    <section id="pricing" className="section section--white" ref={ref}>
      <div className="container">
        <div className="section-head--center">
          <div className="eyebrow" style={{textAlign:'center'}}>{t.pricing.eyebrow}</div>
          <h2 className="section-h2" style={{textAlign:'center'}}>{t.pricing.title}</h2>
          <p className="section-p" style={{textAlign:'center',marginTop:10}}>{t.pricing.sub}</p>
        </div>
        <div className="pricing-grid">
          {plans.map((p,i) => (
            <div key={p.id} className={`pricing-card${p.popular?' pricing-card--pop':''}${vis?' pricing-card--visible':''}`} style={{transitionDelay:`${i*0.1}s`}}>
              {p.popular && <div className="pricing-card__badge">{t.pricing.popular}</div>}
              <div className="pricing-card__name">{lang==='uz' ? p.nameUz||p.name : p.name}</div>
              <div className="pricing-card__price">{lang==='uz' ? p.priceLabelUz||p.priceLabel : p.priceLabel}</div>
              <div className="pricing-card__per">{lang==='uz' ? p.perLabelUz||p.perLabel : p.perLabel}</div>
              <hr className="pricing-card__divider"/>
              <ul className="pricing-card__feats">
                {(lang==='uz' ? p.featuresUz||p.features : p.features).map(f => <li key={f}><span className="pricing-card__check">✓</span>{f}</li>)}
              </ul>
              <button className="pricing-card__btn" onClick={()=>document.getElementById('contact').scrollIntoView({behavior:'smooth'})}>
                {t.pricing.order}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ── FAQ ────────────────────────────────────────────────────────────────── */
function FAQ() {
  const t = useT();
  const [open, setOpen] = useState(null);
  const [ref, vis] = useInView(0.04);
  return (
    <section id="faq" className="section section--light" ref={ref}>
      <div className="container">
        <div className="faq-wrap">
          <div className="faq-left">
            <div className="eyebrow">{t.faq.eyebrow}</div>
            <h2 className="section-h2">{t.faq.title}</h2>
            <p className="section-p" style={{marginBottom:24}}>{t.faq.sub}</p>
            <a href="#contact" className="btn btn--dark">{t.faq.ask}</a>
            <div className="faq-img-box">
              <img src={PHOTOS.faq} alt={t.faq.altClean} style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:16,display:'block'}} loading="lazy"/>
            </div>
          </div>
          <div className="faq-list" style={{borderTop:'1px solid #e5e7eb'}}>
            {t.faq.items.map((faq,i) => {
              const isOpen = open===i;
              return (
                <div key={i} className={`faq-item${vis?' faq-item--visible':''}`} style={{transitionDelay:`${i*0.05}s`}}>
                  <button className="faq-item__q" onClick={()=>setOpen(isOpen?null:i)}>
                    <span>{faq.q}</span>
                    <span className={`faq-item__icon${isOpen?' faq-item__icon--open':''}`}>+</span>
                  </button>
                  {isOpen && <div className="faq-item__a">{faq.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── CTA ────────────────────────────────────────────────────────────────── */
function CTA({ contacts }) {
  const t = useT();
  const phone = contacts?.phone || '+998 77 252-03-30';
  return (
    <div className="cta">
      <img src={PHOTOS.cta} alt="" className="cta__bg" loading="lazy"/>
      <div className="cta__overlay"/>
      <div className="container">
        <div className="cta__inner">
          <h2 className="cta__title">{t.cta.title}</h2>
          <p className="cta__sub">{t.cta.sub}</p>
          <div className="cta__btns">
            <a href="#contact" className="btn btn--dark">{t.cta.btn}</a>
            <a href={`tel:${phone}`} className="btn btn--outline-w">{phone}</a>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── CONTACT ────────────────────────────────────────────────────────────── */
const fStyle = { width:'100%', padding:'12px 16px', border:'1.5px solid #e5e7eb', borderRadius:8, fontSize:'0.92rem', background:'#fff', color:'#06101e', outline:'none', transition:'border-color 0.2s', fontFamily:"'Golos Text',sans-serif" };
const onFocus = e => { e.target.style.borderColor = '#00C2B8'; };
const onBlur  = e => { e.target.style.borderColor = '#e5e7eb'; };

function Contact({ contacts, services }) {
  const t = useT();
  const [form, setForm] = useState({ name:'', phone:'', service:'', area:'', date:'', note:'' });
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const up = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const phone = contacts?.phone    || '+998 77 252-03-30';
  const tg    = contacts?.telegram || 'Master_clean_pro';
  const city  = contacts?.city     || 'Farg\'ona, O\'zbekiston';
  const hours = contacts?.hours    || 'Har kuni, 8:00–21:00';
  const insta = contacts?.instagram || 'master.cleanpro';
  const submit = async () => {
    if (!form.name || !form.phone) { setToast({ msg:t.contact.toastValid, type:'error' }); return; }
    setSending(true);
    try {
      await API.post('/orders', { ...form, service: form.service || services[0]?.title });
      setToast({ msg:t.contact.toastOk, type:'success' });
      setForm({ name:'', phone:'', service:'', area:'', date:'', note:'' });
    } catch { setToast({ msg:t.contact.toastErr, type:'error' }); }
    setSending(false);
  };
  return (
    <section id="contact" className="contact">
      {toast && <Toast msg={toast.msg} type={toast.type} onDone={()=>setToast(null)}/>}
      <div className="contact__left">
        <div className="eyebrow eyebrow--teal">{t.contact.eyebrow}</div>
        <h2 className="contact__h2">{t.contact.title}</h2>
        <p className="contact__desc">{t.contact.desc}</p>
        <div className="contact__items">
          {[
            [t.contact.labelPhone, phone, `tel:${phone}`],
            [t.contact.labelTg, `@${tg}`, `https://t.me/${tg}`],
            [t.contact.labelInsta, `@${insta}`, `https://instagram.com/${insta}`],
            [t.contact.labelAddr, city, null],
            [t.contact.labelHours, hours, null],
          ].map(([label,value,href]) => (
            <div key={label}>
              <span className="contact__item-lbl">{label}</span>
              {href ? <a href={href} target="_blank" rel="noreferrer" className="contact__item-val contact__item-val--link">{value}</a>
                    : <span className="contact__item-val">{value}</span>}
            </div>
          ))}
        </div>
        <div className="contact__photo">
          <img src={PHOTOS.contact} alt={t.contact.altTeam} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} loading="lazy"/>
        </div>
      </div>
      <div className="contact__right">
        <h3 className="contact__form-title">{t.contact.formTitle}</h3>
        <p className="contact__form-sub">{t.contact.formSub}</p>
        <div className="form-row">
          <label>{t.contact.fieldName}<input value={form.name} onChange={up('name')} placeholder="Nilufar" style={fStyle} onFocus={onFocus} onBlur={onBlur}/></label>
          <label>{t.contact.fieldPhone}<input value={form.phone} onChange={up('phone')} placeholder="+998 xx xxx-xx-xx" style={fStyle} onFocus={onFocus} onBlur={onBlur}/></label>
        </div>
        <div className="form-field">
          <span style={{fontWeight:700,fontSize:'0.8rem',color:'#374151',marginBottom:6,display:'block'}}>{t.contact.fieldService}</span>
          <select value={form.service} onChange={up('service')} style={fStyle} onFocus={onFocus} onBlur={onBlur}>
            <option value="">{t.contact.fieldServiceDefault}</option>
            {services.map(s => <option key={s.id} value={s.title}>{s.title}</option>)}
          </select>
        </div>
        <div className="form-row">
          <label>{t.contact.fieldArea}<input type="number" value={form.area} onChange={up('area')} placeholder="45" style={fStyle} onFocus={onFocus} onBlur={onBlur}/></label>
          <label>{t.contact.fieldDate}<input type="date" value={form.date} onChange={up('date')} style={fStyle} onFocus={onFocus} onBlur={onBlur}/></label>
        </div>
        <div className="form-field">
          <span style={{fontWeight:700,fontSize:'0.8rem',color:'#374151',marginBottom:6,display:'block'}}>{t.contact.fieldNote}</span>
          <textarea value={form.note} onChange={up('note')} placeholder={t.contact.fieldNotePlaceholder} style={{...fStyle,minHeight:90,resize:'vertical'}} onFocus={onFocus} onBlur={onBlur}/>
        </div>
        <button onClick={submit} disabled={sending} className={`submit-btn${sending?' submit-btn--loading':''}`}>
          {sending ? t.contact.submitting : t.contact.submit}
        </button>
      </div>
    </section>
  );
}

/* ── FOOTER ─────────────────────────────────────────────────────────────── */
function Footer({ contacts }) {
  const t = useT();
  const phone = contacts?.phone    || '+998 77 252-03-30';
  const tg    = contacts?.telegram || 'Master_clean_pro';
  const insta = contacts?.instagram || 'master.cleanpro';
  const city  = contacts?.city     || 'Farg\'ona, O\'zbekiston';
  const hours = contacts?.hours    || 'Har kuni 8:00–21:00';
  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <div className="footer__logo"><LogoMark size={34} /><span>Master<em>Clean</em> Pro</span></div>
            <p>{t.footer.tagline}</p>
            <div className="footer__socials">
              <a href={`https://instagram.com/${insta}`} target="_blank" rel="noreferrer" aria-label="Instagram"><IconInstagram/></a>
              <a href={`https://t.me/${tg}`} target="_blank" rel="noreferrer" aria-label="Telegram"><IconTelegram/></a>
              <a href={`tel:${phone}`} aria-label="Позвонить"><IconPhone/></a>
            </div>
          </div>
          {[
            [t.footer.colServices, t.footer.serviceItems],
            [t.footer.colCompany,  t.footer.companyItems],
            [t.footer.colContacts, [`☎ ${phone}`,`TG @${tg}`,`IG @${insta}`,`📍 ${city}`,`⏰ ${hours}`]],
          ].map(([h,items]) => (
            <div key={h} className="footer__col">
              <h4>{h}</h4>
              <ul>{items.map(item => <li key={item}><a href="#">{item}</a></li>)}</ul>
            </div>
          ))}
        </div>
        <div className="footer__bottom">
          <span>{t.footer.copy}</span>
          <span>{t.footer.copy2}</span>
        </div>
      </div>
    </footer>
  );
}

/* ── FAB ────────────────────────────────────────────────────────────────── */
function Fab({ contacts }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const phone = contacts?.phone    || '+998 77 252-03-30';
  const tg    = contacts?.telegram || 'Master_clean_pro';
  const hours = contacts?.hours    || 'Ежедн. 8:00–21:00';
  return (
    <div className="fab-wrap">
      {open && (
        <div className="fab-popup">
          <div className="fab-popup__label">{t.fab.label}</div>
          <div className="fab-popup__phone">{phone}</div>
          <div className="fab-popup__hours">{hours}</div>
          <a href={`tel:${phone}`} className="fab-popup__btn fab-popup__btn--call">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .94h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
            {t.fab.call}
          </a>
          <a href={`https://t.me/${tg}`} target="_blank" rel="noreferrer" className="fab-popup__btn fab-popup__btn--tg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L7.19 13.33l-2.965-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.963.229z"/></svg>
            {t.fab.tg}
          </a>
        </div>
      )}
      <button className={`fab-btn${open?' fab-btn--open':''}`} onClick={()=>setOpen(o=>!o)} aria-label="Contact">
        {open
          ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          : <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .94h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
        }
      </button>
    </div>
  );
}

/* ── PAGE ───────────────────────────────────────────────────────────────── */
export default function PublicPage() {
  const [lang, setLang] = useState(() => {
    try { return localStorage.getItem('mc_lang') || 'uz'; } catch { return 'uz'; }
  });
  const [services, setServices] = useState([]);
  const [plans,    setPlans]    = useState([]);
  const [contacts, setContacts] = useState(null);

  useEffect(() => {
    try { localStorage.setItem('mc_lang', lang); } catch {}
  }, [lang]);

  useEffect(() => {
    API.get('/services').then(r=>setServices(r.data)).catch(()=>{});
    API.get('/plans').then(r=>setPlans(r.data)).catch(()=>{});
    API.get('/contacts').then(r=>setContacts(r.data)).catch(()=>{});
  }, []);

  return (
    <LangCtx.Provider value={{ lang, setLang }}>
      <a href="#main" className="skip-link">Перейти к содержимому</a>
      <Navbar contacts={contacts}/>
      <main id="main">
        <Hero contacts={contacts}/>
        <Services/>
        <How/>
        <WhyUs/>
        <Team/>
        <Reviews/>
        <Pricing plans={plans}/>
        <FAQ/>
        <CTA contacts={contacts}/>
        <Contact contacts={contacts} services={services}/>
      </main>
      <Footer contacts={contacts}/>
      <Fab contacts={contacts}/>
    </LangCtx.Provider>
  );
}
